// mow-frontend/src/components/dashboard/widgets/ProblemAssetsWidget.js
// Version: 1.2 — dateRange guard + prop default
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (v1.3 · FlowSense AI Explainer + real-data pass)
//   • "Schedule Maintenance" called analyticsService.createWorkOrder, which did not exist, so it
//     always failed. analyticsService now has createWorkOrder (POST /workorders).
//   • No invented dates: an asset with no last failure showed today's date; it now shows "—".
//   • Severity now includes the failure rate the server sends (it was left out of the score).
//   • Date objects in dateRange are sent as ISO dates (String(date) sent "Sat Oct 24 …").
//   • The fixed "Recommendation / Action" lines are replaced by the FlowSense AI explainer:
//     rule findings from the loaded assets + a plain-language AI explanation on request.
// --- END UPDATE
// MERGE NOTES (v1.1 → v1.2):
//   DATERANGE GUARD:
//   - Prop default: dateRange = { start: null, end: null }
//   - useEffect deps: [dateRange?.start, dateRange?.end, refreshKey] — primitives
//   - Fetch gated: skip + console.warn when start/end missing
//   - String() coercion before passing to service
//   - renderContent shows info Alert when dateRange not ready
//   - Header badge shows active date range
//   CORE LOGIC: ZERO CHANGES
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Heading, Spinner, Alert, AlertIcon, Text, useColorModeValue,
  Flex, HStack, VStack, Icon, IconButton, Tooltip, Badge, Progress,
  Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid, Input,
  InputGroup, InputLeftElement, Select, useDisclosure, Collapse, Code,
  Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Avatar,
  Menu, MenuButton, MenuList, MenuItem, Switch, FormControl, FormLabel,
  StatArrow
} from '@chakra-ui/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import {
  MdRefresh, MdSearch, MdWarning, MdError, MdTrendingUp,
  MdAttachMoney, MdBuild, MdTimer, MdDownload,
  MdFilterList, MdSort, MdViewList, MdViewModule, MdMoreVert
} from 'react-icons/md';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import analyticsService, { asList } from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE (v1.3)
import AssetHistoryModal      from './modals/AssetHistoryModal';
import AssetCostModal         from './modals/AssetCostModal';
import MaintenanceActionModal from './modals/MaintenanceActionModal';

// --- UPDATE (v1.3): Date → YYYY-MM-DD, strings passed through
const asDateParam = (v) => {
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  return String(v);
};
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};
// --- END UPDATE

// --- UPDATE: safe default prop ---
const ProblemAssetsWidget = ({ dateRange = { start: null, end: null } }) => {

  const [problemAssets, setProblemAssets] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [lastRefresh,   setLastRefresh]   = useState(null);
  const [refreshKey,    setRefreshKey]    = useState(0);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [sortBy,         setSortBy]         = useState('totalCost');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [viewMode,       setViewMode]       = useState('table');
  const [showInsights]                     = useState(true); // --- UPDATE: the explainer replaces the dismissable box
  const [selectedAsset,  setSelectedAsset]  = useState(null);
  const [compactView,    setCompactView]    = useState(false);

  const headingColor   = useColorModeValue('gray.800', 'white');
  const mutedColor     = useColorModeValue('gray.600', 'gray.400');
  const cardBg         = useColorModeValue('white', 'gray.800');
  const statBg         = useColorModeValue('gray.50', 'gray.700');
  const borderColor    = useColorModeValue('gray.200', 'gray.600');
  const hoverBg        = useColorModeValue('gray.50', 'gray.700');
  const iconBg         = useColorModeValue('red.50', 'red.900');
  const selectedBg     = useColorModeValue('blue.50', 'blue.900');

  const { isOpen: isAdvancedOpen, onToggle: onToggleAdvanced } = useDisclosure();
  const { isOpen: isHistoryOpen,  onOpen: onHistoryOpen,  onClose: onHistoryClose } = useDisclosure();
  const { isOpen: isCostOpen,     onOpen: onCostOpen,     onClose: onCostClose    } = useDisclosure();
  const { isOpen: isMaintOpen,    onOpen: onMaintOpen,    onClose: onMaintClose   } = useDisclosure();

  const handleScheduleSubmit = async (payload) => {
    try {
      await analyticsService.createWorkOrder(payload);
      onMaintClose();
    } catch (err) {
      throw err instanceof Error ? err : new Error(String(err?.message || err));
    }
  };

  // --- UPDATE: guard + primitive deps + String() coercion ---
  useEffect(() => {
    if (!dateRange?.start || !dateRange?.end) {
      console.warn('[ProblemAssetsWidget] dateRange not ready — fetch skipped.', dateRange);
      setLoading(false);
      return;
    }

    const fetchProblemAssets = async () => {
      try {
        setLoading(true);
        const response = await analyticsService.getProblemAssets({
          startDate: asDateParam(dateRange.start), // --- UPDATE (v1.3)
          endDate:   asDateParam(dateRange.end),
        });

        const rawData = asList(response); // --- UPDATE (v1.3): tolerant of {data:{data:[]}}

        const processedData = rawData.map((asset, index) => ({
          id:          asset.asset_id || `problem-asset-${index}`,
          name:        asset.name || 'Unnamed Asset',
          asset_type:  asset.asset_type || 'General',
          location:    asset.location || 'N/A',
          totalCost:   parseFloat(asset.total_cost) || 0,
          repairCount: parseInt(asset.work_order_count) || 0,
          downtime:    parseFloat(asset.downtime_hours) || 0,
          failureRate: parseFloat(asset.failure_rate) || 0,
          lastFailure: asset.last_failure_date || null, // --- UPDATE (v1.3): was today's date when missing
          severity: calculateSeverity({
            total_cost: asset.total_cost, repair_count: asset.work_order_count,
            downtime_hours: asset.downtime_hours, failure_rate: asset.failure_rate, // --- UPDATE (v1.3)
          }),
        }));

        setProblemAssets(processedData);
        setLastRefresh(new Date());
        setError('');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProblemAssets();
  }, [dateRange?.start, dateRange?.end, refreshKey]);
  // --- END UPDATE ---

  const calculateSeverity = (asset) => {
    const cost = parseFloat(asset.total_cost) || 0;
    const repairs = parseInt(asset.repair_count) || 0;
    const downtime = parseFloat(asset.downtime_hours) || 0;
    const failureRate = parseFloat(asset.failure_rate) || 0;
    const costScore = cost > 10000 ? 30 : cost > 5000 ? 20 : 10;
    const repairScore = repairs > 10 ? 30 : repairs > 5 ? 20 : 10;
    const downtimeScore = downtime > 50 ? 25 : downtime > 20 ? 15 : 5;
    const failureScore = failureRate > 0.5 ? 15 : failureRate > 0.25 ? 10 : 5;
    const totalScore = costScore + repairScore + downtimeScore + failureScore;
    if (totalScore >= 80) return { level: 'critical', color: 'red',    label: 'Critical',     score: totalScore };
    if (totalScore >= 60) return { level: 'high',     color: 'orange', label: 'High Priority', score: totalScore };
    if (totalScore >= 40) return { level: 'medium',   color: 'yellow', label: 'Medium',        score: totalScore };
    return                       { level: 'low',      color: 'green',  label: 'Low',           score: totalScore };
  };

  const filteredAssets = useMemo(() => {
    let filtered = [...problemAssets];
    if (searchTerm) filtered = filtered.filter(a => a.name.toLowerCase().includes(searchTerm.toLowerCase()) || a.asset_type.toLowerCase().includes(searchTerm.toLowerCase()) || a.location.toLowerCase().includes(searchTerm.toLowerCase()));
    if (filterSeverity !== 'all') filtered = filtered.filter(a => a.severity.level === filterSeverity);
    switch (sortBy) {
      case 'totalCost':   filtered.sort((a, b) => b.totalCost   - a.totalCost);   break;
      case 'repairCount': filtered.sort((a, b) => b.repairCount - a.repairCount); break;
      case 'downtime':    filtered.sort((a, b) => b.downtime    - a.downtime);    break;
      case 'failureRate': filtered.sort((a, b) => b.failureRate - a.failureRate); break;
      default: break;
    }
    return filtered.slice(0, 10);
  }, [problemAssets, searchTerm, sortBy, filterSeverity]);

  const statistics = useMemo(() => {
    if (filteredAssets.length === 0) return null;
    const totalCost = filteredAssets.reduce((s, a) => s + a.totalCost, 0);
    const totalRepairs = filteredAssets.reduce((s, a) => s + a.repairCount, 0);
    const totalDowntime = filteredAssets.reduce((s, a) => s + a.downtime, 0);
    const avgFailureRate = filteredAssets.reduce((s, a) => s + a.failureRate, 0) / filteredAssets.length;
    return {
      totalCost: totalCost.toFixed(2), totalRepairs,
      totalDowntime: totalDowntime.toFixed(1),
      avgFailureRate: (avgFailureRate * 100).toFixed(1),
      criticalCount: filteredAssets.filter(a => a.severity.level === 'critical').length,
      highCount:     filteredAssets.filter(a => a.severity.level === 'high').length,
      assetCount:    filteredAssets.length,
    };
  }, [filteredAssets]);

  // --- UPDATE (v1.3): FlowSense rule findings from the loaded assets
  const paExplainer = useMemo(() => {
    const findings = [];
    if (!problemAssets.length) return { findings, facts: {} };
    const crit = problemAssets.filter(a => a.severity.level === 'critical');
    const high = problemAssets.filter(a => a.severity.level === 'high');
    const byCost = [...problemAssets].sort((a, b) => b.totalCost - a.totalCost);
    const total = byCost.reduce((s, a) => s + a.totalCost, 0);
    const top = byCost[0];
    const repeat = problemAssets.filter(a => a.repairCount > 10);
    const failing = problemAssets.filter(a => a.failureRate > 0.5);
    const types = problemAssets.reduce((m, a) => { m[a.asset_type] = (m[a.asset_type] || 0) + 1; return m; }, {});
    const topType = Object.entries(types).sort((a, b) => b[1] - a[1])[0];
    if (crit.length) findings.push({ severity: 'high', text: `${crit.length} asset${crit.length > 1 ? 's score' : ' scores'} critical (80+): ${crit.slice(0, 3).map(a => a.name).join(', ')}${crit.length > 3 ? '…' : ''}.` });
    if (high.length) findings.push({ severity: 'medium', text: `${high.length} asset${high.length > 1 ? 's are' : ' is'} high priority (60–79).` });
    if (top && total > 0 && byCost.length > 1) findings.push({ severity: 'info', text: `${top.name} alone is ${((top.totalCost / total) * 100).toFixed(0)}% of the maintenance cost on this list ($${top.totalCost.toLocaleString()}).` });
    if (repeat.length) findings.push({ severity: 'medium', text: `${repeat.length} asset${repeat.length > 1 ? 's have' : ' has'} more than 10 repairs in the period — candidates for a repair-vs-replace review.` });
    if (failing.length) findings.push({ severity: 'high', text: `${failing.length} asset${failing.length > 1 ? 's have' : ' has'} a failure rate above 50%.` });
    if (topType && topType[1] > 1) findings.push({ severity: 'info', text: `${topType[0]} is the most common type on the list (${topType[1]} assets).` });
    return {
      findings,
      facts: {
        date_range: { start: dateRange?.start ? asDateParam(dateRange.start) : null, end: dateRange?.end ? asDateParam(dateRange.end) : null },
        assets_listed: problemAssets.length, total_cost: +total.toFixed(2),
        severity_rule: 'cost + repairs + downtime + failure-rate points; 80+ critical, 60+ high, 40+ medium',
        assets: byCost.slice(0, 10).map(a => ({ name: a.name, type: a.asset_type, location: a.location, cost: a.totalCost, repairs: a.repairCount, downtime_h: a.downtime, failure_rate: a.failureRate, severity: a.severity.label, score: a.severity.score, last_failure: a.lastFailure })),
      },
    };
  }, [problemAssets, dateRange]);
  // --- END UPDATE

  const handleExport = () => {
    const csv = [['Rank','Asset Name','Type','Location','Total Cost','Repair Count','Downtime (Hours)','Failure Rate','Severity','Last Failure'], ...filteredAssets.map((a, i) => [i+1,a.name,a.asset_type,a.location,a.totalCost.toFixed(2),a.repairCount,a.downtime.toFixed(1),(a.failureRate*100).toFixed(1)+'%',a.severity.label,a.lastFailure||''])].map(r => r.join(',')).join('\n');
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = `problem-assets-top10-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const renderChartView = () => (
    <Box minH="400px">
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={filteredAssets.map(a => ({ name: a.name.length > 15 ? a.name.substring(0,15)+'...' : a.name, cost: a.totalCost, repairs: a.repairCount }))} margin={{ top:20, right:30, left:20, bottom:80 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
          <YAxis />
          <RechartsTooltip contentStyle={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: '8px' }} />
          <Legend />
          <Bar dataKey="cost"    fill="#E53E3E" name="Total Cost ($)" />
          <Bar dataKey="repairs" fill="#ED8936" name="Repair Count"  />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  const renderCardsView = () => (
    <SimpleGrid columns={{ base:1, md:2, lg:3 }} spacing={4}>
      {filteredAssets.map((asset, index) => (
        <Box key={asset.id||`card-${index}`} p={4} bg={selectedAsset?.id===asset.id?selectedBg:statBg} borderRadius="lg" borderWidth="2px" borderColor={`${asset.severity.color}.300`} cursor="pointer" onClick={()=>setSelectedAsset(asset)} _hover={{transform:'translateY(-4px)',boxShadow:'lg',borderColor:`${asset.severity.color}.500`,transition:'all 0.2s'}} transition="all 0.2s">
          <Flex justify="space-between" align="start" mb={3}>
            <HStack><Avatar name={asset.asset_type} size="sm" bg={`${asset.severity.color}.500`} /><VStack align="flex-start" spacing={0}><Text fontWeight="bold" fontSize="sm">{asset.name}</Text><Badge colorScheme={asset.severity.color} fontSize="xs">#{index+1} — {asset.severity.label}</Badge></VStack></HStack>
            <Icon as={MdWarning} color={`${asset.severity.color}.500`} boxSize={5} />
          </Flex>
          <VStack align="stretch" spacing={2} fontSize="sm">
            <HStack justify="space-between"><Text color={mutedColor}>Total Cost:</Text><Text fontWeight="bold" color="red.500">${asset.totalCost.toLocaleString()}</Text></HStack>
            <HStack justify="space-between"><Text color={mutedColor}>Repairs:</Text><Badge colorScheme="orange">{asset.repairCount}</Badge></HStack>
            <HStack justify="space-between"><Text color={mutedColor}>Downtime:</Text><Text>{asset.downtime.toFixed(1)}h</Text></HStack>
            <Stat><HStack justify="space-between"><Text color={mutedColor}>Failure Rate:</Text><HStack spacing={1}><StatArrow type={asset.failureRate>0.5?'increase':'decrease'} /><Text fontWeight="bold" color={asset.failureRate>0.5?'red.500':'yellow.600'}>{(asset.failureRate*100).toFixed(0)}%</Text></HStack></HStack></Stat>
          </VStack>
          <Box mt={3}><Text fontSize="xs" color={mutedColor} mb={1}>Severity Score</Text><Progress value={(asset.severity.score/100)*100} colorScheme={asset.severity.color} size="sm" borderRadius="full" /></Box>
        </Box>
      ))}
    </SimpleGrid>
  );

  const renderTableView = () => (
    <TableContainer borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
      <Table variant="simple" size={compactView?'sm':'md'}>
        <Thead bg={statBg}><Tr><Th>Rank</Th><Th>Asset</Th><Th>Type</Th><Th>Location</Th><Th isNumeric>Total Cost</Th><Th isNumeric>Repairs</Th><Th isNumeric>Downtime</Th><Th>Failure Rate</Th><Th>Severity</Th><Th>Actions</Th></Tr></Thead>
        <Tbody>
          {filteredAssets.map((asset, index) => (
            <Tr key={asset.id||`row-${index}`} _hover={{bg:hoverBg}} bg={selectedAsset?.id===asset.id?selectedBg:'transparent'} cursor="pointer" onClick={()=>setSelectedAsset(asset)}>
              <Td><Badge colorScheme={index<3?'red':index<6?'orange':'gray'} fontSize="sm">#{index+1}</Badge></Td>
              <Td><VStack align="flex-start" spacing={0}><Text fontWeight="medium" fontSize="sm">{asset.name}</Text><Text fontSize="xs" color={mutedColor}>Last: {fmtDate(asset.lastFailure)}</Text></VStack></Td>
              <Td><Badge variant="subtle">{asset.asset_type}</Badge></Td>
              <Td><Text fontSize="sm">{asset.location}</Text></Td>
              <Td isNumeric><Text fontWeight="bold" color="red.500" fontSize="md">${asset.totalCost.toLocaleString()}</Text></Td>
              <Td isNumeric><Badge colorScheme="orange" fontSize="sm">{asset.repairCount}</Badge></Td>
              <Td isNumeric><Text fontSize="sm">{asset.downtime.toFixed(1)}h</Text></Td>
              <Td><HStack><Progress value={asset.failureRate*100} colorScheme={asset.failureRate>0.5?'red':asset.failureRate>0.25?'orange':'yellow'} size="sm" width="60px" borderRadius="full" /><Text fontSize="xs">{(asset.failureRate*100).toFixed(0)}%</Text></HStack></Td>
              <Td><Badge colorScheme={asset.severity.color} fontSize="sm">{asset.severity.label}</Badge></Td>
              <Td>
                <Menu>
                  <MenuButton as={IconButton} icon={<MdMoreVert />} size="sm" variant="ghost" />
                  <MenuList>
                    <MenuItem icon={<MdBuild />}       onClick={(e)=>{e.stopPropagation();setSelectedAsset(asset);onMaintOpen();}}>Schedule Maintenance</MenuItem>
                    <MenuItem icon={<MdTimer />}       onClick={(e)=>{e.stopPropagation();setSelectedAsset(asset);onHistoryOpen();}}>View History</MenuItem>
                    <MenuItem icon={<MdAttachMoney />} onClick={(e)=>{e.stopPropagation();setSelectedAsset(asset);onCostOpen();}}>Cost Breakdown</MenuItem>
                  </MenuList>
                </Menu>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  const renderContent = () => {
    // --- UPDATE: info alert when dateRange not ready ---
    if (!dateRange?.start || !dateRange?.end) return (<Alert status="info" borderRadius="md"><AlertIcon /><Text fontSize="sm">Waiting for date range selection to load asset data...</Text></Alert>);
    if (loading) return (<Flex justify="center" align="center" h="300px" direction="column"><Spinner size="xl" color="red.500" thickness="4px" /><Text mt={4} color={mutedColor}>Loading problem assets...</Text></Flex>);
    if (error && problemAssets.length === 0) return (<Alert status="error" borderRadius="md"><AlertIcon /><VStack align="flex-start" spacing={1}><Text fontWeight="bold">Error Loading Data</Text><Text fontSize="sm">{error}</Text></VStack></Alert>);
    if (problemAssets.length === 0) return (<Alert status="success" borderRadius="md"><AlertIcon /><VStack align="flex-start" spacing={1}><Text fontWeight="bold">No Problem Assets</Text><Text fontSize="sm">All assets are performing within acceptable parameters.</Text></VStack></Alert>);
    if (filteredAssets.length === 0) return (<Alert status="info" borderRadius="md"><AlertIcon /><Text>No assets match your current filters.</Text></Alert>);

    return (
      <VStack spacing={6} align="stretch">
        {statistics && (
          <SimpleGrid columns={{base:2,md:4}} spacing={4}>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}><StatLabel fontSize="xs">Total Cost</StatLabel><StatNumber fontSize="xl" color="red.500">${Number(statistics.totalCost).toLocaleString()}</StatNumber><StatHelpText>Top 10 assets</StatHelpText></Stat>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}><StatLabel fontSize="xs">Total Repairs</StatLabel><StatNumber fontSize="xl">{statistics.totalRepairs}</StatNumber><StatHelpText>Maintenance events</StatHelpText></Stat>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}><StatLabel fontSize="xs">Total Downtime</StatLabel><StatNumber fontSize="xl">{statistics.totalDowntime}h</StatNumber><StatHelpText>Lost productivity</StatHelpText></Stat>
            <Stat bg={statBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}><StatLabel fontSize="xs">Avg Failure Rate</StatLabel><StatNumber fontSize="xl" color="orange.500">{statistics.avgFailureRate}%</StatNumber><StatHelpText>Across all assets</StatHelpText></Stat>
          </SimpleGrid>
        )}
        <Flex gap={3} wrap="wrap" align="center" justify="space-between">
          <HStack spacing={3} flex="1">
            <InputGroup maxW="300px"><InputLeftElement pointerEvents="none"><MdSearch color="gray" /></InputLeftElement><Input placeholder="Search assets..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} size="sm" /></InputGroup>
            <Select value={sortBy} onChange={(e)=>setSortBy(e.target.value)} size="sm" maxW="180px" icon={<MdSort />}><option value="totalCost">By Total Cost</option><option value="repairCount">By Repair Count</option><option value="downtime">By Downtime</option><option value="failureRate">By Failure Rate</option></Select>
            <Select value={filterSeverity} onChange={(e)=>setFilterSeverity(e.target.value)} size="sm" maxW="150px" icon={<MdFilterList />}><option value="all">All Severity</option><option value="critical">Critical Only</option><option value="high">High Priority</option><option value="medium">Medium</option></Select>
            <Badge colorScheme="purple">{filteredAssets.length} / {problemAssets.length} assets</Badge>
          </HStack>
          <HStack spacing={3}>
            <FormControl display="flex" alignItems="center"><FormLabel htmlFor="compact-view" mb="0" fontSize="sm" mr={2}>Compact:</FormLabel><Switch id="compact-view" size="sm" colorScheme="red" isChecked={compactView} onChange={(e)=>setCompactView(e.target.checked)} /></FormControl>
            <HStack spacing={1}>
              <Tooltip label="Table View" hasArrow><IconButton icon={<MdViewList />} size="sm" variant={viewMode==='table'?'solid':'ghost'} colorScheme={viewMode==='table'?'red':'gray'} onClick={()=>setViewMode('table')} aria-label="Table view" /></Tooltip>
              <Tooltip label="Cards View" hasArrow><IconButton icon={<MdViewModule />} size="sm" variant={viewMode==='cards'?'solid':'ghost'} colorScheme={viewMode==='cards'?'red':'gray'} onClick={()=>setViewMode('cards')} aria-label="Cards view" /></Tooltip>
              <Tooltip label="Chart View" hasArrow><IconButton icon={<MdTrendingUp />} size="sm" variant={viewMode==='chart'?'solid':'ghost'} colorScheme={viewMode==='chart'?'red':'gray'} onClick={()=>setViewMode('chart')} aria-label="Chart view" /></Tooltip>
            </HStack>
          </HStack>
        </Flex>
        {/* --- UPDATE (v1.3): FlowSense AI explainer (was fixed recommendation text) */}
        {showInsights && statistics && (
          <FlowSenseAIExplainer
            module="problem_assets"
            title="Explain the problem assets"
            subtitle="Cost, repeat repairs, downtime and failure rate"
            facts={paExplainer.facts}
            findings={paExplainer.findings}
            questions={[
              'Which of these should be replaced rather than repaired?',
              'What is driving the cost on the top asset?',
              'Which work orders should I schedule first?',
            ]}
          />
        )}
        {/* --- END UPDATE */}
        {viewMode==='table'&&renderTableView()}
        {viewMode==='cards'&&renderCardsView()}
        {viewMode==='chart'&&renderChartView()}
        <HStack spacing={4} justify="center" fontSize="sm" p={3} bg={statBg} borderRadius="md">
          <HStack><Box w="12px" h="12px" bg="red.500"    borderRadius="sm" /><Text>Critical (≥80)</Text></HStack>
          <HStack><Box w="12px" h="12px" bg="orange.500" borderRadius="sm" /><Text>High (60–79)</Text></HStack>
          <HStack><Box w="12px" h="12px" bg="yellow.500" borderRadius="sm" /><Text>Medium (40–59)</Text></HStack>
          <HStack><Box w="12px" h="12px" bg="green.500"  borderRadius="sm" /><Text>Low (&lt;40)</Text></HStack>
        </HStack>
      </VStack>
    );
  };

  return (
    <Box bg={cardBg} borderRadius="xl" boxShadow="xl" p={6} borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden" transition="all 0.3s ease" _hover={{boxShadow:'2xl',transform:'translateY(-2px)',transition:'all 0.3s ease'}}>
      <Box position="absolute" top="0" left="0" right="0" height="4px" bgGradient="linear(to-r, red.400, orange.400)" opacity="0.8" />
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={3}>
          <Box p={2} bg={iconBg} borderRadius="lg"><Icon as={MdError} boxSize={6} color="red.500" /></Box>
          <VStack align="flex-start" spacing={0}>
            <Heading size="md" color={headingColor}>Top 10 Problem Assets by Cost</Heading>
            <HStack spacing={2} mt={1} flexWrap="wrap">
              {lastRefresh&&(<Tooltip label={`Last updated: ${lastRefresh.toLocaleString()}`} hasArrow><Badge colorScheme="blue" cursor="help" fontSize="xs">{Math.floor((new Date()-lastRefresh)/60000)}m ago</Badge></Tooltip>)}
              {dateRange?.start&&dateRange?.end&&(<Badge colorScheme="gray" fontSize="xs" variant="outline">{String(dateRange.start)} → {String(dateRange.end)}</Badge>)}
              <Badge colorScheme="red" fontSize="xs">Highest Cost Impact</Badge>
            </HStack>
          </VStack>
        </HStack>
        <HStack spacing={2}>
          <Tooltip label="Export to CSV" hasArrow><IconButton icon={<MdDownload />} size="sm" variant="ghost" onClick={handleExport} isDisabled={!problemAssets.length} aria-label="Export data" _hover={{bg:hoverBg}} /></Tooltip>
          <Tooltip label="Refresh Data" hasArrow><IconButton icon={<MdRefresh />} size="sm" variant="ghost" onClick={()=>setRefreshKey(prev=>prev+1)} isLoading={loading} aria-label="Refresh" _hover={{bg:hoverBg}} /></Tooltip>
        </HStack>
      </Flex>
      <Text fontSize="sm" color={mutedColor} mb={4}>Identifying the most problematic assets based on total maintenance costs, repair frequency, and operational impact</Text>
      {renderContent()}
      {!loading&&!error&&problemAssets.length>0&&(
        <Box mt={4}>
          <Button size="sm" variant="ghost" rightIcon={isAdvancedOpen?<ChevronUpIcon />:<ChevronDownIcon />} onClick={onToggleAdvanced}>Advanced Details</Button>
          <Collapse in={isAdvancedOpen} animateOpacity>
            <Box mt={3} p={4} bg={statBg} borderRadius="md" fontSize="sm">
              <SimpleGrid columns={{base:1,md:2}} spacing={4}>
                <Box><Text fontWeight="bold" mb={2}>Severity Calculation</Text><VStack align="flex-start" spacing={1}><Code fontSize="xs">Cost Score: Based on total maintenance costs</Code><Code fontSize="xs">Repair Score: Number of repair events</Code><Code fontSize="xs">Downtime Score: Total hours out of service</Code><Code fontSize="xs">Failure Score: Percentage failure rate</Code></VStack></Box>
                <Box><Text fontWeight="bold" mb={2}>Data Information</Text><VStack align="flex-start" spacing={1}><Code fontSize="xs">Total Assets: {problemAssets.length}</Code><Code fontSize="xs">Filtered: {filteredAssets.length}</Code><Code fontSize="xs">Date Range: {String(dateRange?.start||'N/A')} → {String(dateRange?.end||'N/A')}</Code><Code fontSize="xs">Last Refresh: {lastRefresh?.toLocaleString()||'N/A'}</Code></VStack></Box>
              </SimpleGrid>
              <Box mt={4}><Text fontWeight="bold" mb={2}>Severity Distribution</Text><SimpleGrid columns={4} spacing={2}>{['critical','high','medium','low'].map((level,i)=>(<Box key={level} textAlign="center"><Badge colorScheme={['red','orange','yellow','green'][i]} fontSize="md" mb={1}>{problemAssets.filter(a=>a.severity.level===level).length}</Badge><Text fontSize="xs" color={mutedColor} textTransform="capitalize">{level}</Text></Box>))}</SimpleGrid></Box>
            </Box>
          </Collapse>
        </Box>
      )}
      <AssetHistoryModal isOpen={isHistoryOpen} onClose={onHistoryClose} asset={selectedAsset} />
      <AssetCostModal    isOpen={isCostOpen}    onClose={onCostClose}    asset={selectedAsset} />
      <MaintenanceActionModal
        isOpen={isMaintOpen}
        onClose={onMaintClose}
        prediction={{ assetId: selectedAsset?.id, assetName: selectedAsset?.name, assetType: selectedAsset?.asset_type, healthStatus: selectedAsset?.severity?.level, riskScore: selectedAsset?.severity?.score, estimatedMaintenanceCost: selectedAsset?.totalCost }}
        onSubmit={handleScheduleSubmit}
      />
    </Box>
  );
};

export default ProblemAssetsWidget;